import type { Shift } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import type { OtsBrand, OtsColorSchemePreference } from "../theme/OtsTheme";
import type { ShiftDraft } from "./shift-form";
import { ShiftFormDialog } from "./ShiftFormDialog";
import { useShiftFormSave } from "./useShiftFormSave";

/** A shift to create or edit: the draft it starts from, and where that came from. */
export type ShiftFormRequest = {
  draft: ShiftDraft;
  /** E.g. "Prefilled from 3 selected tickets." */
  description?: ReactNode;
};

export type ConnectedShiftFormDialogProps = {
  /** Open while set. A draft with an `id` edits that shift. */
  request?: ShiftFormRequest;
  onClose: () => void;
  /** After the save; the shift is already merged into any loaded day of driver activity. */
  onSaved?: (shift: Shift) => void;
  /** The region a new shift is created in, when the draft does not say. */
  regionId?: string;
  timeZone?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The shift form in a dialog, saving through the shift service; the saved shift joins the loaded day it belongs to. */
export const ConnectedShiftFormDialog = ({ request, onClose, onSaved, regionId, timeZone, brand, colorScheme }: ConnectedShiftFormDialogProps) => {
  const draft = request?.draft;
  const close = () => {
    form.clearError();
    onClose();
  };
  const form = useShiftFormSave({
    draft,
    regionId,
    onSaved: (shift) => {
      close();
      onSaved?.(shift);
    },
  });

  return (
    <ShiftFormDialog
      open={!!request}
      mode={form.editing ? "edit" : "new"}
      initialValues={form.initialValues}
      regionId={draft?.regionId ?? regionId}
      description={request?.description}
      odometerUnits={form.odometerUnits}
      showBillableTimes={form.showBillableTimes}
      currentNames={form.currentNames}
      tabletEndDttm={form.stored?.altEndDttm}
      timeZone={timeZone}
      loading={form.loading}
      resetKey={form.stored?.id}
      error={form.error}
      submitting={form.saving}
      onSubmit={(values, names) => void form.submit(values, names)}
      onClose={close}
      brand={brand}
      colorScheme={colorScheme}/>
  );
};
